import type { Steps, Tasks } from '../workflow/schemaTypes';
import { getRetailerFromUrl } from './getRetailerFromUrl';
import { getParams } from './searchParams';
import { getUserprint } from '@narvar/userprint';
import type { TitanEventType } from './titan';

type UserprintResult = Awaited<ReturnType<typeof getUserprint>>;

// Lazy fingerprint storage
let browserIdPromise: Promise<UserprintResult> | null = null;

const getBrowserId = async (): Promise<UserprintResult> => {
  if (!browserIdPromise) {
    browserIdPromise = getUserprint();
  }
  return browserIdPromise;
};

/*
  Titan Client events

  'products_searched',
  'products_list_viewed',
  'products_list_filtered',
  'promotion_viewed',
  'promotion_clicked',
  'product_clicked',
  'product_viewed',
  'product_added',
  'product_removed',
  'cart_viewed',
  'checkout_started',
  'checkout_step_viewed',
  'checkout_step_completed',
  'payment_info_entered',
  'order_completed',
  'order_updated',
  'order_refunded',
  'order_cancelled',
  'coupon_entered',
  'coupon_applied',
  'coupon_denied',
  'coupon_removed',
  'product_added_to_wishlist',
  'product_removed_from_wishlist',
  'wishlist_product_added_to_cart',
  'product_shared',
  'cart_shared',
  'product_reviewed',
  'experiment_viewed',
  'return_ineligible',
 */

type GetData =
  | 'get_order'
  | 'get_delivery_options'
  | 'get_claim'
  | 'get_label'
  | 'get_cart'
  | 'get_retailer_settings';

type GetDataSuccess =
  | 'get_order_success'
  | 'get_delivery_options_success'
  | 'get_claim_success'
  | 'get_label_success'
  | 'get_cart_success'
  | 'get_retailer_settings_success';

type GetDataError =
  | 'get_order_error'
  | 'get_delivery_options_error'
  | 'get_delivery_options_empty'
  | 'get_claim_error'
  | 'get_label_error'
  | 'get_cart_error'
  | 'get_retailer_settings_error';

type LabelPolling = 'get_label_polling' | 'get_label_polling_timeout';

type SetData = 'set_delivery_option' | 'submit' | 'set_postal_code';

type SetDataSuccess =
  | 'set_delivery_option_success'
  | 'submit_success'
  | 'set_postal_code_success';

type SetDataError =
  | 'set_delivery_option_error'
  | 'submit_error'
  | 'set_postal_code_error';

type Generics =
  | 'view'
  | 'click'
  | 'initialization'
  | 'step-viewed'
  | 'redirect'
  | 'task-completed';

export type EventType =
  | TitanEventType // From titan-client
  | GetData
  | GetDataSuccess
  | GetDataError
  | SetData
  | SetDataSuccess
  | SetDataError
  | LabelPolling
  | Generics;

type EventCategory =
  | 'workflow'
  | 'redirect'
  | 'get_data'
  | 'set_data'
  | 'user_action'
  | 'error';

export type PdxEvent = {
  event_category: EventCategory | null;
  event_type?: EventType | null;
  event_value?: string | null;
  event_details?: string | null;
  tag?: `noflake.pdx_fe` | null;
  event_ts?: string | null;
  retailer_moniker?: string | null;
  order_number?: string | null;
  request_id?: string | null;
  data_source?: 'shopify' | 'pdx' | null;
  browser_fingerprint?: string | null;
  page_url?: string | null;
  locale_country?: string | null;
  locale_language?: string | null;
  step?: Steps | null;
  task?: Tasks | null;
  delivery_type?: string | null;
  delivery_method?: string | null;
  packaging_option?: string | null;
  location_id?: string | null;
  claim_status?: string | null;
};

const defaultSet: PdxEvent = {
  event_category: null,
  event_type: null,
  event_value: null,
  tag: null,
  event_ts: null,
  retailer_moniker: null,
  order_number: null,
  request_id: null,
  data_source: null,
  browser_fingerprint: null,
  page_url: null,
  locale_country: null,
  locale_language: null,
  step: null,
  task: null,
  delivery_type: null,
  delivery_method: null,
  packaging_option: null,
  location_id: null,
  claim_status: null,
};

type LocaleParts = { language?: string; country?: string };

const parseLocale = (value?: string): LocaleParts => {
  if (!value) return {};
  const [language, country] = value.split('_');
  return { language, country };
};

export const pdxEvent = async (event: PdxEvent) => {
  const {
    tag: _tag,
    retailer_moniker,
    locale_country,
    locale_language,
    order_number,
    page_url: _page_url,
    data_source,
    event_value,
    event_type,
    event_details,
    ...rest
  } = event;
  const noflakeUrl = import.meta.env.VITE_NOFLAKE_URL;
  const paramLocale = getParams()?.locale;
  const order = getParams()?.order;
  const requestId = getParams()?.request;
  const claimId = getParams()?.claimId;
  const shid = getParams()?.shid;

  const { language, country } = parseLocale(paramLocale);

  const browserId = await getBrowserId();

  const pageUrl =
    typeof window === 'undefined' ? undefined : window.location.href;

  const body = {
    ...defaultSet,
    tag: 'noflake.pdx_fe',
    event_ts: new Date().toISOString(),
    retailer_moniker: retailer_moniker ?? getRetailerFromUrl() ?? shid,
    locale_language: locale_language ?? language ?? 'en',
    locale_country: locale_country ?? country ?? 'US',
    order_number: order_number ?? order,
    request_id: requestId ?? claimId,
    page_url: pageUrl,
    data_source: data_source ?? (shid ? 'shopify' : 'pdx'),
    browser_fingerprint: browserId.browser_fingerprint,
    event_value,
    event_type,
    event_details,
    ...rest,
  };

  // Fire-and-forget analytics beacon: a failed send is not actionable, so
  // swallow rejections rather than let them surface as unhandled errors.
  return fetch(noflakeUrl, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  }).catch(() => undefined);
};
